import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { TOK_LOGO, IMG } from "@/lib/data";

const STORAGE_KEY = "tok_portal_seen_v1";
const DURATION_MS = 1500;

/**
 * Hidden World — first-visit cinematic portal intro.
 * - Plays ONCE per visitor (localStorage flag).
 * - 1.5s total: monogram fades in (450ms), soft gold sigil pulse (500ms),
 *   then dissolves into the homepage hero (550ms).
 * - Pre-loads the hero portal image during playback so the dissolve is seamless.
 * - Honours prefers-reduced-motion (skips entirely).
 * - Pointer-events: none throughout — never blocks interaction.
 */
export default function PortalIntro() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // SSR / first-render guard
    if (typeof window === "undefined") return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const seen = window.localStorage.getItem(STORAGE_KEY);

    if (seen || reduced) return;

    // Pre-warm the hero portal image cache so the dissolve is instant.
    if (IMG?.heroPortal) {
      const img = new Image();
      img.src = IMG.heroPortal;
    }

    setShow(true);

    // Persist the "seen" flag only AFTER the intro has fully played, so a
    // React StrictMode double-invoke doesn't short-circuit the 2nd mount.
    const t = setTimeout(() => {
      setShow(false);
      window.localStorage.setItem(STORAGE_KEY, "1");
    }, DURATION_MS);

    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="portal-intro"
          className="tok-portal-overlay"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: "easeInOut" }}
          aria-hidden="true"
        >
          {/* Soft ambient glow behind monogram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.8, scale: 1.1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(251,191,36,0.10) 0%, transparent 55%)",
            }}
          />

          {/* Expanding gold ring */}
          <motion.span
            className="tok-portal-ring"
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: [0, 0.6, 0], scale: [1, 1.55, 1.85] }}
            transition={{ duration: 1.2, ease: "easeOut", delay: 0.25 }}
          />

          {/* TOK monogram */}
          <motion.img
            src={TOK_LOGO}
            alt=""
            className="tok-portal-monogram relative"
            initial={{ opacity: 0, scale: 0.92, filter: "blur(6px)" }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
            }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
